import { PluginListenerHandle } from "@capacitor/core";
import { registerPlugin } from "@capacitor/core";
import { useState, useRef, useEffect } from "react";
import { useUserSlice } from "@/models/useUserSlice";
import { getPlatform } from "@/models/platform";
import { useUISlice } from "./useUISlice";
import { LocalNotifications } from "@capacitor/local-notifications";
import { useTranslation } from "react-i18next";
import { App } from "@capacitor/app";
// --- Type Definitions (added directly) ---

export interface ChunkData {
  /** Base64 encoded string of the audio data chunk. */
  chunk: string;
}

export type LiveRecordingStatus =
  | "inactive"
  | "recording"
  | "paused"
  | "error"
  | "permissionDenied"
  | "cancelled";

export interface StreamStatus {
  status: LiveRecordingStatus;
  message?: string;
  sampleRate?: number;
  channels?: number;
  bitsPerSample?: number;
}

export interface LiveRecordingPlugin {
  startStream(options?: { chunkIntervalSeconds?: number }): Promise<void>;
  stopStream(): Promise<void>;
  pauseStream(): Promise<void>;
  resumeStream(): Promise<void>;
  cancelStream(): Promise<void>;
  addListener(
    eventName: "onChunkData",
    listenerFunc: (data: ChunkData) => void
  ): Promise<PluginListenerHandle> & PluginListenerHandle;
  addListener(
    eventName: "onStreamStatusChange",
    listenerFunc: (status: StreamStatus) => void
  ): Promise<PluginListenerHandle> & PluginListenerHandle;
  addListener(
    eventName: "onInterruptionBegan",
    listenerFunc: () => void
  ): Promise<PluginListenerHandle> & PluginListenerHandle;
  addListener(
    eventName: "onInterruptionEnded",
    listenerFunc: () => void
  ): Promise<PluginListenerHandle> & PluginListenerHandle;
  removeAllListeners(): Promise<void>;
}

// --- End Type Definitions ---

const LiveRecording = registerPlugin<LiveRecordingPlugin>("LiveRecording");

export const useLiveRecorderIOS = () => {
  const status = useUISlice(
    (state) => state.liveRecording?.status || "inactive"
  );
  const statusListener = useRef<PluginListenerHandle | null>(null);
  const chunkListener = useRef<PluginListenerHandle | null>(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const startTimeRef = useRef<number | null>(null);
  const durationRef = useRef<number>(0);
  const timerIntervalRef = useRef<NodeJS.Timeout>();
  const wasRecordingBeforeInactive = useRef<boolean>(false);
  const { t } = useTranslation();

  // Add a ref to store the format for synchronous access in listeners
  const audioFormatRef = useRef<{
    sampleRate?: number;
    channels?: number;
    bitsPerSample?: number;
  }>({});

  // Add listener for app state changes
  useEffect(() => {
    const stateChangeListenerPromise = App.addListener(
      "appStateChange",
      ({ isActive }) => {
        console.log(`App state changed: isActive = ${isActive}`);

        if (!isActive) {
          // App going to background, check if we're recording
          wasRecordingBeforeInactive.current = status === "recording";

          if (wasRecordingBeforeInactive.current) {
            console.log("App went inactive while recording");
            // Store current duration before going inactive
            if (startTimeRef.current) {
              durationRef.current += Date.now() - startTimeRef.current;
              startTimeRef.current = null;
            }
          }
        } else if (wasRecordingBeforeInactive.current && status === "paused") {
          // App becoming active again and recording was interrupted
          console.log("App became active again, resuming recording");
          resumeRecording();
          wasRecordingBeforeInactive.current = false;
        }
      }
    );

    return () => {
      stateChangeListenerPromise.then((listener) => {
        listener.remove();
      });
    };
  }, [status]);

  const removeListeners = async () => {
    console.log("Attempting to remove listeners");
    if (statusListener.current) {
      await statusListener.current.remove();
      statusListener.current = null;
      console.log("Status listener removed");
    }

    if (chunkListener.current) {
      await chunkListener.current.remove();
      chunkListener.current = null;
      console.log("Chunk listener removed");
    }
  };

  const cleanup = async () => {
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = undefined;
    }
    startTimeRef.current = null;
    durationRef.current = 0;
    setElapsedTime(0);
    audioFormatRef.current = {}; // Clear audio format ref
    await removeListeners();
  };

  useEffect(() => {
    if (status === "recording") {
      timerIntervalRef.current = setInterval(() => {
        if (startTimeRef.current) {
          const elapsedTime =
            Date.now() - startTimeRef.current + durationRef.current;
          setElapsedTime(elapsedTime);
        }
      }, 1000);
    } else {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = undefined;
      }
      if (
        status === "inactive" ||
        status === "cancelled" ||
        status === "error" ||
        status === "permissionDenied"
      ) {
        setElapsedTime(0);
      }
    }

    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = undefined;
      }
    };
  }, [status]);

  const addListeners = async () => {
    await removeListeners();

    console.log("Attempting to add listeners");

    try {
      statusListener.current = await LiveRecording.addListener(
        "onStreamStatusChange",
        (info: StreamStatus) => {
          // info now includes potential audio format
          console.log("Stream Status Changed:", info);
          useUISlice.getState().setLiveRecording({
            ...useUISlice.getState().liveRecording,
            status: info.status,
          });

          // Capture audio format details when recording starts
          if (
            info.status === "recording" &&
            info.sampleRate &&
            info.channels &&
            info.bitsPerSample
          ) {
            console.log("Received Audio Format:", info);
            const format = {
              sampleRate: info.sampleRate,
              channels: info.channels,
              bitsPerSample: info.bitsPerSample,
            };
            useUserSlice
              .getState()
              .note()
              .liveRecordingChunk({
                action: "setFormat",
                noteId: useUISlice.getState().liveRecording?.noteId,
                format: { deviceType: getPlatform(), ...format },
              });
            audioFormatRef.current = format; // Update ref synchronously
          }

          if (
            ["cancelled", "error", "permissionDenied"].includes(info.status)
          ) {
            console.log(`Status is ${info.status}, removing listeners.`);
            removeListeners(); // Keep listener removal logic
          }
        }
      );
      console.log("Status listener added");

      chunkListener.current = await LiveRecording.addListener(
        "onChunkData",
        (data: ChunkData) => {
          // Use the ref for the synchronous check
          if (audioFormatRef.current.sampleRate) {
            useUserSlice
              .getState()
              .note()
              .liveRecordingChunk({
                action: "addChunk",
                noteId: useUISlice.getState().liveRecording?.noteId,
                chunk: Array.from(
                  Uint8Array.from(atob(data.chunk), (c) => c.charCodeAt(0))
                ),
              });
          } else {
            console.warn(
              "Received chunk data before audio format ref was set. Ignoring chunk."
            );
          }
        }
      );
      console.log("Chunk listener added");

      LiveRecording.addListener("onInterruptionBegan", () => {
        console.log("[Event received] Interruption began");
        // Save current duration when interruption happens
        if (startTimeRef.current) {
          durationRef.current += Date.now() - startTimeRef.current;
          startTimeRef.current = null;
        }
      });

      LiveRecording.addListener("onInterruptionEnded", async () => {
        console.log("[Event received] Interruption ended");
        const appState = await App.getState();

        if (appState.isActive) {
          await LiveRecording.resumeStream();
          startTimeRef.current = Date.now(); // Restart the timer measurement

          return;
        }
        LocalNotifications.schedule({
          notifications: [
            {
              title: t("useLiveRecorder.interruptionEndedTitle"),
              body: t("useLiveRecorder.interruptionEndedBody"),
              id: Math.floor(Math.random() * 90000) + 10000,
            },
          ],
        });
      });
    } catch (error) {
      console.error("Failed to add listeners:", error);
      audioFormatRef.current = {}; // Reset ref on error
      await removeListeners();
    }
  };

  const startRecording = async () => {
    if (status === "recording" || status === "paused") return;

    const permission = await LocalNotifications.checkPermissions();

    if (permission.display.includes("prompt")) {
      useUISlice.getState().setAlertModal({
        title: t("useLiveRecorder.permissionRequired"),
        subtitle: t("useLiveRecorder.permissionRequiredSubtitle"),
        onDismiss: () => {
          useUISlice.getState().setAlertModal(undefined);
          startRecording();
        },
        button: {
          text: t("useLiveRecorder.grant"),
          onPress: async () => {
            await LocalNotifications.requestPermissions();
            useUISlice.getState().setAlertModal(undefined);
            startRecording();
          },
        },
      });
      return;
    }

    try {
      audioFormatRef.current = {}; // Clear previous format ref
      await addListeners(); // Add listeners first
      startTimeRef.current = Date.now();
      durationRef.current = 0;
      setElapsedTime(0);
      await LiveRecording.startStream({ chunkIntervalSeconds: 15 });
    } catch (error) {
      console.error("Failed to start recording:", error);
      useUISlice.getState().setLiveRecording({
        ...useUISlice.getState().liveRecording,
        status: "error",
      });
      await cleanup(); // Ensure cleanup happens
    }
  };

  const stopRecording = async (callback: () => void) => {
    if (status !== "recording" && status !== "paused") return;
    try {
      await LiveRecording.stopStream();
      await cleanup();

      const noteId = useUISlice.getState().liveRecording?.noteId;

      if (!noteId) return;

      useUserSlice.getState().note().update(noteId, {
        status: "upload_started",
      });

      callback();
    } catch (error) {
      console.error("Failed to stop recording:", error);
      useUISlice.getState().setLiveRecording({
        ...useUISlice.getState().liveRecording,
        status: "error",
      });
      await cleanup(); // Ensure cleanup on error
    }
  };

  const pauseRecording = async () => {
    if (status !== "recording") return;

    try {
      await LiveRecording.pauseStream();
      if (startTimeRef.current) {
        durationRef.current += Date.now() - startTimeRef.current;
        startTimeRef.current = null;
      }
    } catch (error) {
      console.error("Failed to pause recording:", error);
      useUISlice.getState().setLiveRecording({
        ...useUISlice.getState().liveRecording,
        status: "error",
      });
    }
  };

  const resumeRecording = async () => {
    if (status !== "paused") return;

    try {
      await LiveRecording.resumeStream();
      startTimeRef.current = Date.now(); // Restart the timer measurement
    } catch (error) {
      console.error("Failed to resume recording:", error);
      useUISlice.getState().setLiveRecording({
        ...useUISlice.getState().liveRecording,
        status: "error",
      });
    }
  };

  const cancelRecording = async (callback: () => void) => {
    if (status !== "recording" && status !== "paused") {
      callback();
      return;
    }

    try {
      await LiveRecording.cancelStream();
      await cleanup(); // Call cleanup explicitly here too
    } catch (error) {
      console.error("Failed to cancel recording:", error);
      useUISlice.getState().setLiveRecording({
        ...useUISlice.getState().liveRecording,
        status: "error",
      });
      await cleanup(); // Ensure cleanup on error
    }

    callback();
  };

  return {
    status,
    elapsedTime,
    pauseOrPlay: status === "paused" ? resumeRecording : pauseRecording,
    startRecording,
    stopRecording,
    pauseRecording,
    resumeRecording,
    cancelRecording,
  };
};
